<template>
  <div class="suppliers-page">
    <PageHeader
      :title="text.page.title"
      :subtitle="text.page.subtitle"
      :actions="headerActions"
      @action="onHeaderAction"
    />

    <div class="suppliers-content">
      <section class="metrics-grid">
        <CustomersKpiCard
          v-for="metric in visibleMetrics"
          :key="metric.title"
          :title="metric.title"
          :value="metric.value"
          :value-route="metric.valueRoute"
          :value-link-class="metric.valueLinkClass"
          :value-meta="metric.valueMeta"
          :truncate-value="metric.truncateValue"
          :value-scale="metric.valueScale"
          :icon="metric.icon"
          :icon-bg="metric.iconBg"
          :icon-color="metric.iconColor"
        />
      </section>

      <SuppliersToolbar
        @filters-change="handleFiltersChange"
        @search-change="handleSearchChange"
        @apply="applyToolbarFilters"
        @toggle-view="toggleViewMode"
      />

      <SuppliersTable
        :rows="suppliersRows"
        :loading="tableLoading"
        :pagination="suppliersPagination"
        :capabilities="tableCapabilities"
        :empty-text="text.table.empty"
        @page-change="handlePageChange"
        @delete-supplier="handleDeleteSupplier"
        @toggle-supplier-status="handleToggleSupplierStatus"
        @edit-supplier="handleEditSupplier"
      />
    </div>

    <SupplierModal
      :show="isSupplierModalOpen"
      :mode="supplierModalMode"
      :initial-data="supplierModalData"
      :initial-section-key="supplierModalSection"
      @close="closeSupplierModal"
      @submit="handleSupplierModalSubmit"
      @delete="handleSupplierModalDelete"
    />

    <SupplierImportModal
      :show="isSupplierImportModalOpen"
      @close="closeSupplierImportModal"
      @success="handleSupplierImportSuccess"
    />

    <ExportFormatModal
      :show="isSupplierExportModalOpen"
      :filters="activeFilters"
      :search-term="searchTerm"
      :page-customer-ids="currentPageSupplierIds"
      :text-catalog="text"
      :export-service="exportService"
      file-name-prefix="proveedores"
      @close="closeSupplierExportModal"
    />

    <ConfirmActionModal
      :show="isConfirmActionModalOpen"
      :tone="confirmActionTone"
      :loading="isConfirmActionLoading"
      :title="confirmActionTitle"
      :subtitle="confirmActionSubtitle"
      :message="confirmActionMessage"
      :confirm-text="confirmActionButton"
      :cancel-text="text.actions.common.cancel"
      @confirm="confirmPendingAction"
      @cancel="closeConfirmActionModal"
    />
  </div>
</template>

<script>
import PageHeader from '@/components/common/PageHeader.vue';
import CustomersKpiCard from '@/components/customers/CustomersKpiCard.vue';
import SuppliersToolbar from '@/components/suppliers/SuppliersToolbar.vue';
import SuppliersTable from '@/components/suppliers/SuppliersTable.vue';
import SupplierModal from '@/components/suppliers/SupplierModal.vue';
import SupplierImportModal from '@/components/suppliers/imports/SupplierImportModal.vue';
import ExportFormatModal from '@/components/common/exports/ExportFormatModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import suppliersService from '@/services/suppliers';
import { CAPABILITIES_UPDATED_EVENT } from '@/utils/capabilities';
import { extractPayload } from '@/utils/dataNormalization';
import {
  createDefaultSupplierFilters,
  normalizeSupplierFilters,
  buildSuppliersListParams,
  filterSuppliersRowsLocal,
} from '@/composables/suppliers/useSuppliersFilters';
import { fetchSuppliersListPayload } from '@/composables/suppliers/useSuppliersList';
import {
  createInitialSuppliersMetricsValues,
  mapSuppliersMetricsPayload,
  buildSuppliersKpiCards,
} from '@/composables/suppliers/useSuppliersMetrics';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('suppliers');
const SUPPLIERS_SYNC_COOLDOWN_MS = 60 * 60 * 1000;

export default {
  name: 'SuppliersPage',
  components: {
    PageHeader,
    CustomersKpiCard,
    SuppliersToolbar,
    SuppliersTable,
    SupplierModal,
    SupplierImportModal,
    ExportFormatModal,
    ConfirmActionModal,
  },
  data() {
    return {
      text,
      isSupplierModalOpen: false,
      isSupplierImportModalOpen: false,
      isSupplierExportModalOpen: false,
      isConfirmActionModalOpen: false,
      pendingActionType: null,
      pendingActionSupplier: null,
      pendingActionSource: null,
      isSupplierSyncRunning: false,
      supplierModalMode: 'create',
      supplierModalData: null,
      supplierModalSection: 'general',
      supplierModalSaving: false,
      headerActionsBase: [
        {
          key: 'new-supplier',
          label: text.actions.header.newSupplier,
          icon: 'fa-plus',
          variant: 'primary',
        },
        {
          key: 'import-suppliers',
          label: text.actions.header.importSuppliers,
          icon: 'fa-upload',
          variant: 'secondary',
        },
        {
          key: 'export-suppliers',
          label: text.actions.header.exportSuppliers,
          icon: 'fa-download',
          variant: 'secondary',
          className: 'app-header-btn--export',
        },
        {
          key: 'sync-suppliers',
          label: text.actions.header.syncSuppliers,
          icon: 'fa-refresh',
          variant: 'secondary',
          className: 'app-header-btn--sync',
          iconOnly: true,
          title: text.confirm.sync.title,
        },
      ],
      headerActions: [],
      suppliersAllRows: [],
      suppliersRows: [],
      suppliersPagination: {
        current_page: 1,
        per_page: 20,
        total_pages: 1,
        total_items: 0,
      },
      tableLoading: false,
      viewMode: 'table',
      searchTerm: '',
      listRequestToken: 0,
      activeFilters: createDefaultSupplierFilters(),
      metricsValues: createInitialSuppliersMetricsValues(),
    };
  },
  computed: {
    exportService() {
      return suppliersService;
    },
    tableCapabilities() {
      const can = typeof this.$can === 'function' ? this.$can : () => false;

      return {
        canViewDetail: can('providers.list'),
        canDelete: can('providers.button_delete'),
        canEdit: can('providers.button_edit'),
      };
    },
    visibleMetrics() {
      return buildSuppliersKpiCards(this.metricsValues);
    },
    currentPageSupplierIds() {
      return (Array.isArray(this.suppliersRows) ? this.suppliersRows : [])
        .map((row) => Number(row?.id || 0))
        .filter((id) => id > 0);
    },
    confirmActionTitle() {
      if (this.pendingActionType === 'sync-suppliers') {
        return text.confirm.sync.title;
      }

      if (this.pendingActionType === 'toggle-status') {
        const willDisable = Boolean(this.pendingActionSupplier?.active);
        return willDisable ? text.confirm.toggleStatus.titleDisable : text.confirm.toggleStatus.titleActivate;
      }

      return text.confirm.delete.title;
    },
    confirmActionSubtitle() {
      if (this.pendingActionType === 'sync-suppliers') {
        return text.confirm.sync.subtitle;
      }

      if (this.pendingActionType === 'toggle-status') {
        return text.confirm.toggleStatus.subtitle;
      }

      return text.confirm.delete.subtitle;
    },
    confirmActionMessage() {
      if (this.pendingActionType === 'sync-suppliers') {
        return text.confirm.sync.message;
      }

      const supplierName = String(this.pendingActionSupplier?.name || '').trim() || text.common.noName;

      if (this.pendingActionType === 'toggle-status') {
        const willDisable = Boolean(this.pendingActionSupplier?.active);
        return willDisable
          ? text.confirm.toggleStatus.messageDisable(supplierName)
          : text.confirm.toggleStatus.messageActivate(supplierName);
      }

      return text.confirm.delete.message(supplierName);
    },
    confirmActionButton() {
      if (this.pendingActionType === 'sync-suppliers') {
        return text.confirm.sync.confirm;
      }

      if (this.pendingActionType === 'toggle-status') {
        return this.pendingActionSupplier?.active ? text.actions.common.disable : text.actions.common.activate;
      }

      return text.actions.common.delete;
    },
    confirmActionTone() {
      if (this.pendingActionType === 'sync-suppliers') {
        return 'info';
      }

      return 'warning';
    },
    isConfirmActionLoading() {
      return this.pendingActionType === 'sync-suppliers' && this.isSupplierSyncRunning;
    },
  },
  mounted() {
    this.refreshHeaderActions();
    window.addEventListener(CAPABILITIES_UPDATED_EVENT, this.refreshHeaderActions);
    this.fetchSuppliers();
  },
  beforeUnmount() {
    window.removeEventListener(CAPABILITIES_UPDATED_EVENT, this.refreshHeaderActions);
  },
  methods: {
    areFiltersEqual(left, right) {
      const normalizedLeft = normalizeSupplierFilters(left);
      const normalizedRight = normalizeSupplierFilters(right);

      return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
    },
    buildSuppliersParams() {
      return buildSuppliersListParams({
        filters: this.activeFilters,
      });
    },
    syncSuppliersRows() {
      const filteredRows = filterSuppliersRowsLocal(this.suppliersAllRows, this.searchTerm);

      this.suppliersRows = filteredRows;
      this.suppliersPagination = {
        ...this.suppliersPagination,
        current_page: 1,
        total_pages: 1,
        total_items: filteredRows.length,
      };
    },
    async fetchSuppliers() {
      const requestToken = ++this.listRequestToken;
      this.tableLoading = true;

      try {
        const payload = await fetchSuppliersListPayload(
          suppliersService,
          this.buildSuppliersParams(),
          this.suppliersPagination,
        );

        if (requestToken === this.listRequestToken) {
          this.suppliersAllRows = Array.isArray(payload.data) ? payload.data : [];
          this.metricsValues = mapSuppliersMetricsPayload(payload.metrics || {});
          this.syncSuppliersRows();
        }
      } catch (error) {
        if (requestToken === this.listRequestToken) {
          this.suppliersAllRows = [];
          this.metricsValues = createInitialSuppliersMetricsValues();
          this.syncSuppliersRows();
        }
      } finally {
        if (requestToken === this.listRequestToken) {
          this.tableLoading = false;
        }
      }
    },
    refreshHeaderActions() {
      this.headerActions = this.headerActionsBase.filter((action) => {
        if (action?.key === 'new-supplier') {
          return this.$can('providers.button_new');
        }

        if (action?.key === 'sync-suppliers') {
          return this.$can('providers.button_edit');
        }

        return true;
      });

      this.headerActions = this.headerActions.map((action) => {
        if (action?.key !== 'sync-suppliers') {
          return action;
        }

        return {
          ...action,
          loading: this.isSupplierSyncRunning,
          disabled: this.isSupplierSyncRunning,
        };
      });
    },
    onHeaderAction(action) {
      const key = String(action?.key || '').trim();

      if (key === 'new-supplier') {
        this.openCreateSupplierModal();
        return;
      }

      if (key === 'import-suppliers') {
        this.isSupplierImportModalOpen = true;
        return;
      }

      if (key === 'export-suppliers') {
        this.isSupplierExportModalOpen = true;
        return;
      }

      if (key === 'sync-suppliers') {
        this.openSupplierSyncConfirmModal();
      }
    },
    resolveSupplierSyncStorageKey() {
      const tenantKey = String(window?.location?.hostname || '').trim() || 'global';
      return `suppliers.last_sync_financials_at.${tenantKey}`;
    },
    readLastSupplierSyncAt() {
      const raw = localStorage.getItem(this.resolveSupplierSyncStorageKey());
      const parsed = Number(raw || 0);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    },
    writeLastSupplierSyncAt(timestamp) {
      localStorage.setItem(this.resolveSupplierSyncStorageKey(), String(Number(timestamp || 0)));
    },
    closeSupplierExportModal() {
      this.isSupplierExportModalOpen = false;
    },
    closeSupplierImportModal() {
      this.isSupplierImportModalOpen = false;
    },
    handleSupplierImportSuccess() {
      this.fetchSuppliers();
    },
    resolveSupplierSyncRemainingMinutes() {
      const lastAt = this.readLastSupplierSyncAt();
      if (lastAt <= 0) {
        return 0;
      }

      const elapsed = Date.now() - lastAt;
      const remaining = SUPPLIERS_SYNC_COOLDOWN_MS - elapsed;
      if (remaining <= 0) {
        return 0;
      }

      return Math.max(1, Math.ceil(remaining / (60 * 1000)));
    },
    canRunSupplierSyncNow() {
      return this.resolveSupplierSyncRemainingMinutes() <= 0;
    },
    openSupplierSyncConfirmModal() {
      if (this.isSupplierSyncRunning) {
        return;
      }

      if (!this.canRunSupplierSyncNow()) {
        const minutes = this.resolveSupplierSyncRemainingMinutes();
        this.$toast?.info(text.toast.syncCooldown(minutes));
        return;
      }

      this.openConfirmActionModal('sync-suppliers', null);
    },
    resolveEntityPayload(response) {
      const payload = extractPayload(response);

      if (payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object') {
        return payload.data;
      }

      return payload && typeof payload === 'object' ? payload : null;
    },
    resolveResponseMessage(response, fallbackMessage) {
      return response?.message || response?.data?.message || fallbackMessage;
    },
    openCreateSupplierModal() {
      this.supplierModalMode = 'create';
      this.supplierModalData = null;
      this.supplierModalSection = 'general';
      this.isSupplierModalOpen = true;
    },
    closeSupplierModal(force = false) {
      if (this.supplierModalSaving && !force) {
        return;
      }

      this.isSupplierModalOpen = false;
      this.supplierModalMode = 'create';
      this.supplierModalData = null;
      this.supplierModalSection = 'general';
    },
    async handleEditSupplier(supplier) {
      const supplierId = Number(supplier?.id || 0);
      if (!supplierId) {
        return;
      }

      try {
        const response = await suppliersService.show(supplierId);
        const entity = this.resolveEntityPayload(response);

        this.supplierModalMode = 'edit';
        this.supplierModalData = entity;
        this.supplierModalSection = 'general';
        this.isSupplierModalOpen = true;
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || text.toast.loadSupplierError;
        this.$toast?.error(message);
      }
    },
    async handleSupplierModalSubmit(payload) {
      if (this.supplierModalSaving) {
        return;
      }

      const mode = String(payload?.mode || 'create').toLowerCase();
      const supplierId = Number(payload?.id || 0);
      const supplierPayload = payload?.payload || {};
      const specialtyIds = Array.isArray(payload?.specialtyIds) ? payload.specialtyIds : [];

      this.supplierModalSaving = true;

      try {
        let response;
        if (mode === 'edit' && supplierId > 0) {
          response = await suppliersService.update(supplierId, supplierPayload);
        } else {
          response = await suppliersService.store(supplierPayload);
        }

        const savedEntity = this.resolveEntityPayload(response) || {};
        const persistedSupplierId = Number(savedEntity?.id || supplierId || 0);
        const canSyncSpecialties = typeof this.$can === 'function'
          ? this.$can('providers.button_edit')
          : true;

        if (persistedSupplierId > 0 && canSyncSpecialties) {
          try {
            await suppliersService.skillsStore(persistedSupplierId, {
              skills: specialtyIds,
              replace: true,
            });
          } catch (skillsError) {
            this.$toast?.error(text.toast.specialtiesSyncWarning);
          }
        }

        this.closeSupplierModal(true);
        this.fetchSuppliers();
        this.$toast?.success(
          this.resolveResponseMessage(
            response,
            mode === 'edit'
              ? text.toast.updateSupplierSuccess
              : text.toast.saveSupplierSuccess,
          ),
        );
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || text.toast.saveSupplierError;
        this.$toast?.error(message);
      } finally {
        this.supplierModalSaving = false;
      }
    },
    async handleSupplierModalDelete(supplier) {
      const supplierId = Number(supplier?.id || this.supplierModalData?.id || 0);
      if (!supplierId) {
        return;
      }

      const supplierPayload = {
        id: supplierId,
        name: String(supplier?.name || this.supplierModalData?.name || '').trim() || text.common.noName,
        active: Number(supplier?.active ?? this.supplierModalData?.active ?? 1),
      };

      this.openConfirmActionModal('delete', supplierPayload, 'modal');
    },
    handleSearchChange(search) {
      this.searchTerm = String(search || '').trim();
      this.syncSuppliersRows();
    },
    handleFiltersChange(filters) {
      const nextFilters = normalizeSupplierFilters({
        ...this.activeFilters,
        ...(filters || {}),
      });

      if (this.areFiltersEqual(this.activeFilters, nextFilters)) {
        return;
      }

      this.activeFilters = nextFilters;
    },
    handlePageChange(nextPage) {
      void nextPage;
    },
    async handleDeleteSupplier(supplier) {
      const supplierId = Number(supplier?.id || 0);
      if (!supplierId) {
        return;
      }

      this.openConfirmActionModal('delete', supplier);
    },
    handleToggleSupplierStatus(supplier) {
      const supplierId = Number(supplier?.id || 0);
      if (!supplierId) {
        return;
      }

      this.openConfirmActionModal('toggle-status', supplier);
    },
    openConfirmActionModal(actionType, supplier, source = 'table') {
      this.pendingActionType = String(actionType || '');
      this.pendingActionSupplier = supplier || null;
      this.pendingActionSource = String(source || 'table');
      this.isConfirmActionModalOpen = true;
    },
    closeConfirmActionModal() {
      if (this.pendingActionType === 'sync-suppliers' && this.isSupplierSyncRunning) {
        return;
      }

      this.isConfirmActionModalOpen = false;
      this.pendingActionType = null;
      this.pendingActionSupplier = null;
      this.pendingActionSource = null;
    },
    async confirmPendingAction() {
      if (this.pendingActionType === 'sync-suppliers') {
        await this.confirmSupplierSyncAction();
        return;
      }

      const supplier = this.pendingActionSupplier;
      if (!supplier?.id) {
        this.closeConfirmActionModal();
        return;
      }

      let success = false;

      try {
        if (this.pendingActionType === 'toggle-status') {
          const nextStatus = Number(supplier?.active || 0) === 1 ? 0 : 1;
          const response = await suppliersService.toggleStatus(supplier.id, { active: nextStatus });
          this.$toast?.success(this.resolveResponseMessage(response, text.toast.toggleStatusSuccess));
        } else {
          const response = await suppliersService.delete(supplier.id);

          if (this.pendingActionSource === 'modal') {
            this.closeSupplierModal(true);
          }

          this.$toast?.success(this.resolveResponseMessage(response, text.toast.deleteSuccess));
        }

        success = true;
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || text.toast.actionError;
        this.$toast?.error(message);
      } finally {
        this.closeConfirmActionModal();

        if (success) {
          this.fetchSuppliers();
        }
      }
    },
    async confirmSupplierSyncAction() {
      if (this.isSupplierSyncRunning) {
        return;
      }

      if (!this.canRunSupplierSyncNow()) {
        const minutes = this.resolveSupplierSyncRemainingMinutes();
        this.closeConfirmActionModal();
        this.$toast?.info(text.toast.syncCooldown(minutes));
        return;
      }

      this.isSupplierSyncRunning = true;
      this.refreshHeaderActions();

      try {
        const response = await suppliersService.syncFinancials();

        this.writeLastSupplierSyncAt(Date.now());
        this.$toast?.success(response?.message || text.toast.syncSuccess);
        this.fetchSuppliers();
      } catch (error) {
        const message = error?.response?.data?.message || text.toast.syncError;
        this.$toast?.error(message);
      } finally {
        this.isSupplierSyncRunning = false;
        this.refreshHeaderActions();
        this.closeConfirmActionModal();
      }
    },
    toggleViewMode() {
      this.viewMode = this.viewMode === 'table' ? 'table-alt' : 'table';
    },
    applyToolbarFilters() {
      this.suppliersPagination = {
        ...this.suppliersPagination,
        current_page: 1,
      };

      this.fetchSuppliers();
    },
  },
};
</script>

<style scoped>
.suppliers-page {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.suppliers-content {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

@media (max-width: 767px) {
  .suppliers-content {
    padding: 12px;
  }
}
</style>
